import { motion } from 'motion/react';
import { ReadinessGate } from '../../../../components/common/readiness-gate/index.js';
import { DitheringShaderFill } from '../../../../components/ui/dithering-shader/dithering-shader-fill.js';

/**
 * One-shot editor splash screen.
 *
 * Plays for exactly `SPLASH_DURATION_MS` (1500ms) on the initial mount of
 * `EditorPage`, non-skippable, and is never replayed during iteration.
 * The master timeline:
 *
 *   0ms    → live swirl backdrop (DitheringShaderFill) + radial vignette
 *   0-600  → "Agent" typewriter reveal via CSS steps(5)
 *   600-800 → "Brush" springs in from the right (light weight, dimmed)
 *   800-1100 → terminal cursor block blinks once
 *   1100-1500 → canvas-color layer fades in over the swirl, covering
 *               the splash before ReadinessGate unmounts it
 *
 * Gate logic is delegated to `<ReadinessGate>`: `onComplete` fires only
 * when BOTH the 1500ms timer has elapsed AND the caller-supplied
 * `canComplete` prop is true.
 *
 * Honors `prefers-reduced-motion` (the shader pins speed to 0 → static
 * frame; the spring/typewriter collapse to plain renders).
 */

export const SPLASH_DURATION_MS = 1500;

export interface SplashScreenProps {
  /**
   * Called once after SPLASH_DURATION_MS has elapsed AND canComplete is true.
   * Called exactly once per mount (guarded by an internal ref).
   */
  onComplete: () => void;
  /**
   * Readiness gate. SplashScreen starts its 1500ms timer immediately on mount,
   * but does NOT fire `onComplete` until BOTH the timer has elapsed AND
   * canComplete is true. Defaults to `true` (no external gating).
   * `EditorPage` passes `!isLoading && (imagePreloaded || errored)` so the
   * splash holds on slow networks but still releases on errors.
   */
  canComplete?: boolean;
}

// Snapshot the reduced-motion preference once at module load to match the
// pattern used by `celebration-moment.tsx` and `skyrim-loading.tsx`. This is
// intentional — it makes the fallback deterministic under tests that do
// `vi.resetModules()` + re-import after mocking `window.matchMedia`.
const prefersReducedMotion =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

// Inline keyframe styles for the typewriter reveal and cursor blink. CSS
// `steps()` is the correct primitive for character-by-character reveal — no
// runtime JS required. Kept in a single string so the `<style>` tag renders
// once per splash mount and the browser dedupes it via its style cache.
const typewriterStyles = `
  @keyframes splashTypeAgent {
    from { width: 0; }
    to   { width: 5ch; }
  }
  .splash-type-agent {
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    vertical-align: bottom;
    width: 5ch;
    animation: splashTypeAgent 600ms steps(5, end) forwards;
  }
  @keyframes splashBlink {
    0%   { opacity: 1; }
    49%  { opacity: 1; }
    50%  { opacity: 0; }
    100% { opacity: 1; }
  }
  .splash-cursor-block {
    display: inline-block;
    width: 0.6em;
    height: 1em;
    background-color: var(--color-accent-cyan);
    vertical-align: bottom;
    margin-left: 0.1em;
    animation: splashBlink 300ms steps(2) 800ms 1 forwards;
  }
`;

export function SplashScreen({ onComplete, canComplete = true }: SplashScreenProps) {
  return (
    <ReadinessGate
      minDisplayMs={SPLASH_DURATION_MS}
      canComplete={canComplete}
      onComplete={onComplete}
    >
      <style>{typewriterStyles}</style>
      <div
        role="status"
        aria-live="polite"
        aria-label="AgentBrush — loading editor"
        data-testid="splash-screen"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          backgroundColor: 'var(--color-canvas)',
        }}
      >
        <DitheringShaderFill
          shape="swirl"
          type="4x4"
          colorBack="#0a0a0a"
          colorFront="#2de1fc"
          pxSize={3}
          speed={0.6}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(10,10,10,0.75), rgba(10,10,10,0.35) 55%, transparent 85%)',
          }}
        />
        <div
          aria-hidden="true"
          data-testid="splash-logo"
          style={{
            display: 'flex',
            alignItems: 'center',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-display)',
            color: 'var(--color-text-primary)',
            letterSpacing: '0.02em',
            position: 'relative',
            zIndex: 2,
          }}
        >
          {prefersReducedMotion ? (
            <>
              <span style={{ fontWeight: 600 }}>Agent</span>
              <span style={{ fontWeight: 300, color: 'var(--color-text-secondary)' }}>Brush</span>
              <span
                style={{
                  display: 'inline-block',
                  width: '0.6em',
                  height: '1em',
                  backgroundColor: 'var(--color-accent-cyan)',
                  verticalAlign: 'bottom',
                  marginLeft: '0.1em',
                }}
              />
            </>
          ) : (
            <>
              <span className="splash-type-agent" style={{ fontWeight: 600 }}>
                Agent
              </span>
              <motion.span
                initial={{ x: 24, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{
                  delay: 0.6,
                  duration: 0.2,
                  ease: [0.34, 1.56, 0.64, 1],
                }}
                style={{
                  display: 'inline-block',
                  fontWeight: 300,
                  color: 'var(--color-text-secondary)',
                }}
              >
                Brush
              </motion.span>
              <span className="splash-cursor-block" />
            </>
          )}
        </div>
        <motion.div
          aria-hidden="true"
          data-testid={prefersReducedMotion ? 'splash-fade-layer' : 'splash-dither-layer'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            delay: prefersReducedMotion ? 1.2 : 1.1,
            duration: prefersReducedMotion ? 0.3 : 0.4,
            ease: 'easeOut',
          }}
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'var(--color-canvas)',
            pointerEvents: 'none',
            zIndex: 3,
          }}
        />
      </div>
    </ReadinessGate>
  );
}
